import {
  BID_LEVEL_BIN_COUNT,
  blendLikelihood,
  computeConfidence,
  createEmptyBehaviorModel,
  normalizeBidLevel,
  normalizeBidPass,
  normalizeTrump,
  personalizationWeight,
  trumpCategoryIndex,
  type BehaviorModel,
} from './behavior-model.js';
import type { HumanHandLearningObservation } from './human-action-observations.js';
import type { HumanPublicAction } from './human-action-observations.js';
import { extractHandFeatures } from './hand-feature-extractor.js';
import type { Domino } from '../types';

export interface AdaptiveProfileLearningState {
  profileId: string;
  completedHands: number;
  confidence: number;
  updatedAt: string;
  behaviorModel?: BehaviorModel;
}

function isAggressiveBid(bidValue: number | null): boolean {
  return bidValue !== null && bidValue >= 35;
}

export function updateBehaviorModel(
  model: BehaviorModel,
  observation: HumanHandLearningObservation,
): BehaviorModel {
  const next = JSON.parse(JSON.stringify(model)) as BehaviorModel;

  for (const bid of observation.bidding) {
    if (bid.passed) {
      next.bidPass.pass += 1;
    } else {
      next.bidPass.bid += 1;
      if (bid.bidValue !== null) {
        const index = Math.min(Math.max(bid.bidValue - 30, 0), BID_LEVEL_BIN_COUNT - 1);
        next.bidLevelBins[index] = (next.bidLevelBins[index] ?? 0) + 1;
      }
    }

    const bucket = next.riskByMarks[bid.marksContext];
    if (isAggressiveBid(bid.bidValue)) {
      bucket.aggressive += 1;
    } else {
      bucket.conservative += 1;
    }
  }

  if (observation.trump) {
    const index = trumpCategoryIndex(observation.trump.declaration, observation.trump.suit);
    next.trumpCounts[index] = (next.trumpCounts[index] ?? 0) + 1;
  }

  for (const play of observation.plays) {
    if (play.earlyTrumpLead) next.earlyTrumpLead.yes += 1;
    else next.earlyTrumpLead.no += 1;

    if (play.countSlough) next.countSlough.yes += 1;
    else next.countSlough.no += 1;

    if (play.partnerWinning) {
      if (play.overtookPartner) next.partnerWinOvertake.overtakes += 1;
      else next.partnerWinOvertake.supports += 1;
    }
  }

  return next;
}

export function updateAdaptiveProfile(
  profile: AdaptiveProfileLearningState,
  observation: HumanHandLearningObservation,
): AdaptiveProfileLearningState {
  const behaviorModel = updateBehaviorModel(
    profile.behaviorModel ?? createEmptyBehaviorModel(),
    observation,
  );
  const completedHands = profile.completedHands + 1;

  return {
    ...profile,
    completedHands,
    confidence: computeConfidence(completedHands),
    updatedAt: new Date().toISOString(),
    behaviorModel,
  };
}

function uniformPublicActionLikelihood(): number {
  return 1;
}

function personalizedPublicActionLikelihood(
  model: BehaviorModel,
  action: HumanPublicAction,
): number {
  if (action.phase === 'bidding') {
    const bidPass = normalizeBidPass(model);
    if (action.passed) return bidPass.pass;
    const bidLikelihood = bidPass.bid;
    if (action.bidValue === null || action.bidValue === undefined) return bidLikelihood;
    return bidLikelihood * normalizeBidLevel(model, action.bidValue);
  }

  if (action.phase === 'trump_selection' && action.trump) {
    const index = trumpCategoryIndex(action.trump.declaration, action.trump.suit);
    return normalizeTrump(model, index);
  }

  if (action.phase === 'playing' && action.play) {
    const early = action.play.earlyTrumpLead
      ? model.earlyTrumpLead.yes / (model.earlyTrumpLead.yes + model.earlyTrumpLead.no)
      : model.earlyTrumpLead.no / (model.earlyTrumpLead.yes + model.earlyTrumpLead.no);
    const slough = action.play.countSlough
      ? model.countSlough.yes / (model.countSlough.yes + model.countSlough.no)
      : model.countSlough.no / (model.countSlough.yes + model.countSlough.no);
    const partner = action.play.overtookPartner
      ? model.partnerWinOvertake.overtakes /
        (model.partnerWinOvertake.overtakes + model.partnerWinOvertake.supports)
      : model.partnerWinOvertake.supports /
        (model.partnerWinOvertake.overtakes + model.partnerWinOvertake.supports);
    return (early + slough + partner) / 3;
  }

  return 1;
}

export function scorePublicActionsLikelihood(
  model: BehaviorModel | undefined,
  confidence: number,
  publicActions: HumanPublicAction[],
): number {
  if (!model || publicActions.length === 0 || confidence <= 0) {
    return 1;
  }

  let likelihood = 1;
  for (const action of publicActions) {
    const uniform = uniformPublicActionLikelihood();
    const personalized = personalizedPublicActionLikelihood(model, action);
    likelihood *= blendLikelihood(uniform, personalized, confidence);
  }

  return Math.max(likelihood, 1e-6);
}

/**
 * Score a candidate hidden human hand using only public actions and learned tendencies.
 * The sampled hand is never inserted into AI observation — it is scored in isolation.
 */
export function scoreCandidateHumanHandLikelihood(
  model: BehaviorModel | undefined,
  confidence: number,
  publicActions: HumanPublicAction[],
  candidateHand: Domino[],
  marksBefore: [number, number],
  humanTeam = 0,
): number {
  const actionLikelihood = scorePublicActionsLikelihood(model, confidence, publicActions);
  if (!model || confidence <= 0) {
    return actionLikelihood;
  }

  const features = extractHandFeatures(candidateHand, marksBefore, humanTeam);
  const longSuitBonus = Math.max(...features.suitLengths) / 7;
  const controlBonus = features.highControls / 7;
  const featureLikelihood = 0.5 + 0.25 * longSuitBonus + 0.25 * controlBonus;

  return actionLikelihood * featureLikelihood;
}

export function resolveBehaviorModel(snapshot?: BehaviorModel): BehaviorModel | undefined {
  if (!snapshot) return undefined;
  return snapshot;
}

export function shouldUseBehaviorWeights(
  confidence: number,
  completedHands: number,
  model?: BehaviorModel,
): boolean {
  return completedHands > 0 && confidence > 0 && model !== undefined;
}

export function effectivePersonalizationWeight(confidence: number): number {
  return personalizationWeight(confidence);
}
